<script>
import { Bar } from "vue-chartjs";
import Chart from "chart.js";

const isStackedBar = (chart) => {
  const scales = chart.options && chart.options.scales;
  return !!(scales && scales.xAxes && scales.xAxes[0] && scales.xAxes[0].stacked);
};

const drawValueLabel = (ctx, value, x, y) => {
  ctx.save();
  ctx.fillStyle = "#666666";
  ctx.font = "700 12px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillText(value, x, y);
  ctx.restore();
};

Chart.plugins.register({
  id: "barValueLabels",
  afterDatasetsDraw: function (chart) {
    if (chart.config.type !== "bar") return;

    const ctx = chart.ctx;

    if (!isStackedBar(chart)) {
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta.hidden) return;

        meta.data.forEach((bar, index) => {
          const value = dataset.data[index];
          if (value === null || value === undefined) return;

          drawValueLabel(ctx, value, bar._model.x, bar._model.y - 4);
        });
      });
      return;
    }

    // Stacked bars: draw only the total at the top of each stack so the
    // per-cycle values don't overlap.
    const totals = new Array(chart.data.labels.length).fill(0);
    const topBars = new Array(chart.data.labels.length).fill(null);

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((bar, index) => {
        totals[index] += dataset.data[index] || 0;
        topBars[index] = bar;
      });
    });

    topBars.forEach((bar, index) => {
      if (!bar || !totals[index]) return;
      drawValueLabel(ctx, totals[index], bar._model.x, bar._model.y - 4);
    });
  },
});

// Chart.js v2 has no built-in option to add a gap between the legend and the
// plot area (`labels.padding` only pushes the text down). This plugin grows
// the legend box below the labels, keeping the text in place while moving the
// chart away. Enable it per chart with `legend.gap`.
Chart.plugins.register({
  id: "legendSpacing",
  beforeLayout: function (chart) {
    const legend = chart.legend;
    const legendOptions = chart.options.legend;

    if (!legend || !legendOptions || !legendOptions.display) {
      return;
    }

    const gap = legendOptions.gap || 0;

    legend.afterFit = function () {
      legend.height += gap;
      legend.minSize.height += gap;
    };
  },
});

const mergeOptions = (base, extra) => {
  const out = { ...base };

  Object.keys(extra).forEach((key) => {
    const value = extra[key];

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      out[key] = mergeOptions(base[key], value);
    } else {
      out[key] = value;
    }
  });

  return out;
};

export default {
  name: "UiChart",
  extends: Bar,
  props: ["data", "options"],
  data() {
    return {
      chartOptions: {
        responsive: true,

        maintainAspectRatio: false,
        showLines: false,
        layout: {
          padding: {
            top: 20,
          },
        },
        legend: {
          display: false,
        },
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                min: 0,
                precision: 0,
                suggestedMax: 5,
              },
            },
          ],
        },
      },
    };
  },
  computed: {
    mergedOptions() {
      if (!this.options) {
        return this.chartOptions;
      }
      return mergeOptions(this.chartOptions, this.options);
    },
  },
  initialize() {
    Chart.types.Bar.prototype.initialize.apply(this, arguments);
    var ctx = document.getElementById("myChart").getContext("2d");

    if (this.options.curvature !== undefined && this.options.curvature <= 1) {
      var rectangleDraw = this.datasets[0].bars[0].draw;
      var self = this;
      var radius =
        this.datasets[0].bars[0].width * this.options.curvature * 0.5;

      // override the rectangle draw with ours
      this.datasets.forEach(function (dataset) {
        dataset.bars.forEach(function (bar) {
          bar.draw = function () {
            // draw the original bar a little down (so that our curve brings it to its original position)
            var y = bar.y;
            // the min is required so animation does not start from below the axes
            bar.y = Math.min(bar.y + radius, self.scale.endPoint - 1);
            // adjust the bar radius depending on how much of a curve we can draw
            var barRadius = bar.y - y;
            rectangleDraw.apply(bar, arguments);

            // draw a rounded rectangle on top
            Chart.helpers.drawRoundedRectangle(
              self.chart.ctx,
              bar.x - bar.width / 2,
              bar.y - barRadius + 1,
              bar.width,
              bar.height,
              barRadius
            );
            ctx.fill();

            // restore the y value
            bar.y = y;
          };
        });
      });
    }
  },
  mounted() {
    this.renderChart(this.data, this.mergedOptions);
  },
};
</script>

<style scoped>
/* Estilos opcionais para o componente de gráfico */
</style>
